import { useCallback, useRef, useState } from 'react';
import { api } from '../../api';
import type { ImportLimits } from '../../types';
import { formatBytes } from '../../lib/format';

/** Files per request: keeps each request's memory bounded and progress smooth. */
const FILES_PER_REQUEST = 10;

interface Selected {
  key: string;
  file: File;
  /** Why this file will not be uploaded, decided before sending anything. */
  problem: string | null;
}

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i === -1 ? '' : name.slice(i).toLowerCase();
}

function checkFile(file: File, limits: ImportLimits | null): string | null {
  if (file.size === 0) return 'This file is empty.';
  if (limits && !limits.extensions.includes(extOf(file.name))) {
    return 'Not a supported photo or video format.';
  }
  if (limits && file.size > limits.maxFileBytes) {
    return `Larger than the ${formatBytes(limits.maxFileBytes)} limit.`;
  }
  return null;
}

/**
 * Device upload: drag and drop or pick files, see what will be skipped and
 * why, then upload in small requests that share one import batch. Per-file
 * progress after the bytes arrive is shown in the Recent imports list.
 */
export default function DeviceImport({
  limits,
  onUploaded,
}: {
  limits: ImportLimits | null;
  onUploaded: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<Selected[]>([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sentBytes, setSentBytes] = useState(0);
  const [failures, setFailures] = useState<Array<{ name: string; reason: string }>>([]);

  const addFiles = useCallback(
    (incoming: FileList | null) => {
      if (!incoming) return;
      setFailures([]);
      setSelected((prev) => {
        const seen = new Set(prev.map((s) => s.key));
        const next = [...prev];
        for (const file of Array.from(incoming)) {
          const key = `${file.name}:${file.size}:${file.lastModified}`;
          if (seen.has(key)) continue; // the same file picked twice
          seen.add(key);
          next.push({ key, file, problem: checkFile(file, limits) });
        }
        return next;
      });
    },
    [limits],
  );

  const valid = selected.filter((s) => !s.problem);
  const skipped = selected.filter((s) => s.problem);
  const totalBytes = valid.reduce((n, s) => n + s.file.size, 0);

  const upload = async () => {
    if (valid.length === 0) return;
    setUploading(true);
    setSentBytes(0);
    setFailures([]);
    const batchId = crypto.randomUUID();
    const failed: Array<{ name: string; reason: string }> = [];
    let doneBytes = 0;

    for (let i = 0; i < valid.length; i += FILES_PER_REQUEST) {
      const chunk = valid.slice(i, i + FILES_PER_REQUEST).map((s) => s.file);
      const chunkBytes = chunk.reduce((n, f) => n + f.size, 0);
      try {
        await api.upload(chunk, {
          batchId,
          onProgress: (loaded) => setSentBytes(doneBytes + Math.min(loaded, chunkBytes)),
        });
      } catch (err) {
        // Keep going: one bad request must not lose the rest of the batch.
        for (const f of chunk) failed.push({ name: f.name, reason: (err as Error).message });
      }
      doneBytes += chunkBytes;
      setSentBytes(doneBytes);
      onUploaded(); // the server's per-file items appear as each chunk lands
    }

    setFailures(failed);
    setSelected([]);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const pct = totalBytes > 0 ? Math.round((sentBytes / totalBytes) * 100) : 0;

  return (
    <div className="flex h-full flex-col">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!uploading) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!uploading) addFiles(e.dataTransfer.files);
        }}
        className={`flex flex-1 flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
          dragging ? 'border-accent bg-accent/5' : 'border-white/15'
        }`}
      >
        <p className="text-base font-semibold">Drop photos and videos here</p>
        <p className="mt-1 mb-5 text-sm text-white/45">
          JPG, PNG, HEIC, WebP, MP4, MOV and more
          {limits ? `, up to ${Math.round(limits.maxFileBytes / 1024 / 1024)} MB each` : ''}
        </p>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="focus-ring rounded-md bg-white px-6 py-2.5 text-sm font-bold text-black transition hover:bg-white/85 disabled:opacity-40"
        >
          Choose photos &amp; videos
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          className="sr-only"
          tabIndex={-1}
          aria-label="Choose photos and videos"
          onChange={(e) => addFiles(e.target.files)}
        />
      </div>

      {selected.length > 0 && !uploading && (
        <div className="mt-5">
          <div className="mb-2 flex items-baseline justify-between text-sm">
            <span className="text-white/70">
              {valid.length} ready to upload ({formatBytes(totalBytes)})
              {skipped.length > 0 && <span className="text-amber-200/80">, {skipped.length} will be skipped</span>}
            </span>
            <button
              onClick={() => setSelected([])}
              className="focus-ring rounded text-xs text-white/45 hover:text-white/80"
            >
              Clear
            </button>
          </div>
          <ul className="max-h-48 space-y-1 overflow-y-auto pr-1 text-xs">
            {selected.map((s) => (
              <li key={s.key} className="flex items-center gap-3">
                <span className={`min-w-0 flex-1 truncate ${s.problem ? 'text-white/35 line-through' : 'text-white/60'}`}>
                  {s.file.name}
                </span>
                {s.problem ? (
                  <span className="shrink-0 text-amber-200/80">{s.problem}</span>
                ) : (
                  <span className="shrink-0 text-white/30">{formatBytes(s.file.size)}</span>
                )}
                <button
                  onClick={() => setSelected((prev) => prev.filter((p) => p.key !== s.key))}
                  className="focus-ring shrink-0 rounded px-1 text-white/30 hover:text-white/80"
                  aria-label={`Remove ${s.file.name}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <button
            onClick={upload}
            disabled={valid.length === 0}
            className="focus-ring mt-4 w-full rounded-md bg-accent py-3 text-sm font-bold text-white transition hover:bg-accent-soft disabled:opacity-40"
          >
            Upload {valid.length} {valid.length === 1 ? 'file' : 'files'}
          </button>
        </div>
      )}

      {uploading && (
        <div className="mt-5" role="status">
          <div className="mb-1.5 flex justify-between text-sm text-white/70">
            <span>Uploading {valid.length} {valid.length === 1 ? 'file' : 'files'}</span>
            <span>{pct}%</span>
          </div>
          <div className="h-1 overflow-hidden rounded-full bg-white/10">
            <div className="h-full bg-accent transition-[width] duration-300" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-2 text-xs text-white/40">Keep this tab open until the upload finishes.</p>
        </div>
      )}

      {failures.length > 0 && (
        <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-100" role="alert">
          <p className="mb-1 font-semibold">{failures.length} {failures.length === 1 ? 'file' : 'files'} didn’t upload</p>
          <ul className="space-y-0.5">
            {failures.map((f) => (
              <li key={f.name}>
                {f.name}: {f.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
